import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  spacing: 8,
  typography: {
    fontFamily: 'Urbanist',
    h1: {
      fontSize: '96px',
      fontStyle: 'normal',
      fontWeight: 300,
      lineHeight: '112px',
      letterSpacing: '-1.5px',
    },
    h2: {
      fontSize: '60px',
      fontStyle: 'normal',
      fontWeight: 300,
      lineHeight: '72px',
      letterSpacing: '-0.5px',
    },
    h3: {
      fontSize: '48px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '56px',
    },
    h4: {
      fontSize: '34px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '42px',
      letterSpacing: '0.25px',
    },
    h5: {
      fontSize: '24px',
      fontStyle: 'normal',
      fontWeight: 500,
      lineHeight: '24px',
    },
    h6: {
      fontSize: '20px',
      fontStyle: 'normal',
      fontWeight: 500,
      lineHeight: '26px',
      letterSpacing: '0.15px',
    },
    subtitle1: {
      fontSize: '16px',
      fontStyle: 'normal',
      fontWeight: 600,
      lineHeight: '28px',
      letterSpacing: '0.15px',
    },
    subtitle2: {
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: 500,
      lineHeight: '22px',
      letterSpacing: '0.1px',
    },
    body1: {
      fontSize: '16px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '24px',
      letterSpacing: '0.15px',
    },
    body2: {
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '20px',
      letterSpacing: '0.15px',
    },
    button: {
      large: {
        fontSize: '15px',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: '26px',
        letterSpacing: '0.46px',
        textAlign: 'left',
      },
      medium: {
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: '24px',
        letterSpacing: '0.4px',
        textAlign: 'left',
      },
      small: {
        fontSize: '13px',
        fontStyle: 'normal',
        fontWeight: 800,
        lineHeight: '22px',
        letterSpacing: '0.46px',
        textAlign: 'left',
      },
    },
    caption: {
      fontSize: '12px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '20px',
      letterSpacing: '0.4px',
    },
    overline: {
      fontSize: '12px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '32px',
      letterSpacing: '1px',
      textTransform: 'uppercase',
    },
  },
  palette: {
    primary: {
      main: '#A3E5F4',
      dark: '#4FBBDE',
      light: '#CDCEFE',
      contrastText: '#FFF',
    },
    secondary: {
      main: '#5C5C5C',
      dark: '#6AC8E6',
      light: '#B1B1B1',
      contrastText: '#fff',
    },
    text: {
      primary: '#fff',
      secondary: '#878787',
      disabled: 'rgba(19,29,41,0.38)',
    },
    info: {
      main: 'rgba(65, 135, 169, 0.2)',
      dark: '#1976D2',
      light: '#64B5F6',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#0F0F0F',
    },
    error: {
      main: '#CF3A2F',
      dark: '#990A00',
      light: '#FF7066',
    },
    warning: {
      main: '#FFAB00',
      light: '#FFCD66',
      dark: '#DD8502',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#04C89D',
      light: '#9BFDD4',
      dark: '#13B18E',
    },
    divider: '#EAEAEA',
  },
  components: {
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-root': {
            paddingTop: '16.5px',
            paddingBottom: '16.5px',
            paddingLeft: '14px',
            paddingRight: '14px',

            width: 'Fill (358px)px',
            height: 'Hug (56px)px',
            gap: '0px',
            borderRadius: '8px 0px 0px 0px',
            border: '1px 0px 0px 0px',
            opacity: '0px',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          },
          '&.MuiInputBase-adornedEnd': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          },
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          fontSize: '16px',
          lineHeight: '150%',
          letterSpacing: '0.15px',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          width: 'Fill (358px)px',
          height: 'Hug (56px)px',
          gap: '0px',
          borderRadius: '8px 0px 0px 0px',
          border: '1px 0px 0px 0px',
          opacity: '0px',
          '& .MuiInputBase-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          },
          '&.MuiInputBase-adornedEnd': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          },
          '& input:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 1000px #1e1e1e inset',
            WebkitTextFillColor: '#ffffff',
            backgroundColor: '#1e1e1e',
            color: '#ffffff',
          },
          '& input:autofill': {
            backgroundColor: '#1e1e1e',
            color: '#ffffff',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          boxShadow:
            '0px 1px 5px 0px #0000001F, box-shadow: 0px 2px 2px 0px #00000024, box-shadow: 0px 3px 1px -2px #00000033',
          padding: '8px 22px',
          fontSize: '14px',
          lineHeight: '24px',
          letterSpacing: '0.4px',
          flex: 'none',
          flexGrow: 0,
          margin: '0px 0px',
          textTransform: 'none',
          weight: 600,
        },
        containedPrimary: {
          background: 'linear-gradient(90deg, #A3E5F4 0%, #4187A9 100%)',
        },
        sizeLarge: {
          fontSize: '15px',
          lineHeight: '26px',
          letterSpacing: '0.46px',
        },
        text: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
          '&:hover': {
            color: 'white',
          },
        },
        outlined: {
          '&:disabled': {
            boxShadow: 'none',
          },
        },
      },
    },
    MuiButtonGroup: {
      styleOverrides: {
        root: {
          height: '36px',
          width: '250px',
          borderRadius: '4px',
        },
        groupedContained: {
          backgroundColor: 'red',
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          boxShadow:
            '0px 3px 5px -1px rgba(0, 0, 0, 0.2), 0px 6px 10px rgba(0, 0, 0, 0.14), 0px 1px 18px rgba(0, 0, 0, 0.12)',
        },
        secondary: {
          color: '#FFFFFF',
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: 'rgba(0, 0, 0, 0.54)',
          '&$disabled': {
            color: 'rgba(0, 0, 0, 0.26)',
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            '& .MuiSvgIcon-root': {
              background: 'linear-gradient(90deg, #A3E5F4 0%, #4187A9 100%)',
              borderRadius: '4px',
            },
          },
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          '&.MuiPickersArrowSwitcher-leftArrowIcon, &.MuiPickersArrowSwitcher-rightArrowIcon':
            {
              color: 'white',
            },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          color: '#fff',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#0F0F0F',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '16px',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: '19px',
          letterSpacing: '0.15px',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          height: '56px',

          borderBottom: '1px solid rgba(224, 224, 224, 1)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: ' #151517',
          borderRadius: '24px',
          boxShadow:
            '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px rgba(0, 0, 0, 0.14), 0px 1px 5px rgba(0, 0, 0, 0.12)',

          padding: '16px 24px 16px 24px',
          gap: '0px',
          opacity: '0px',
        },
        outlined: {
          border: '1px solid rgba(0, 0, 0, 0.12)',
          boxShadow: 'none',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderWidth: '1px',
          borderStyle: 'solid',
          borderRadius: '8px',

          background: 'linear-gradient(90deg, #A3E5F4 0%, #4187A9 100%)',
        },
        bar: {
          background: 'linear-gradient(90deg, #A3E5F4 0%, #4187A9 100%)',
          borderRadius: '8px',
        },
      },
    },
  },
});

export default theme;
